import type { StudioApi } from '../useTechnicalStudio';
import { FIB_USE_CASES } from '@/mocks/technical';
import { fmtPrice, NumberField, SectionHeading } from './ui';

const PIP = 0.0001;

export default function FibonacciSection({ api }: { api: StudioApi }) {
  const { fib, setFib, fibLevels } = api;
  const current = fib.current;

  // Find nearest level
  const nearest = [...fibLevels].sort((a, b) => Math.abs(a.price - current) - Math.abs(b.price - current))[0];
  const nearestDist = nearest ? (current - nearest.price) / PIP : 0;

  return (
    <section id="fibonacci" className="scroll-mt-24">
      <SectionHeading
        eyebrow="Công cụ 2"
        title="Máy tính Fibonacci"
        subtitle="Tính các mức Fibonacci Retracement và Extension từ một vùng giá tham chiếu."
      />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Inputs */}
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
          <h3 className="font-display text-base font-bold text-slate-900">Vùng giá tham chiếu</h3>
          <div className="mt-5 space-y-4">
            <NumberField label="Swing High" value={fib.high} onChange={(v) => setFib({ ...fib, high: v })} />
            <NumberField label="Swing Low" value={fib.low} onChange={(v) => setFib({ ...fib, low: v })} />
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Xu hướng</span>
              <div className="flex rounded-xl bg-slate-100 p-1">
                {(['up', 'down'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFib({ ...fib, trend: t })}
                    className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                      fib.trend === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t === 'up' ? 'Uptrend' : 'Downtrend'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Loại Fibonacci</span>
              <div className="flex rounded-xl bg-slate-100 p-1">
                {(['retracement', 'extension'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFib({ ...fib, type: t })}
                    className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                      fib.type === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t === 'retracement' ? 'Retracement' : 'Extension'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Current position */}
          <div className="mt-6 rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Giá hiện tại</span>
              <input
                type="number"
                step={0.0001}
                value={current}
                onChange={(e) => setFib({ ...fib, current: parseFloat(e.target.value) || 0 })}
                className="w-28 rounded-lg border border-slate-200 bg-white px-2 py-1 text-right font-mono text-sm font-bold text-slate-800 outline-none focus:border-violet-400"
              />
            </div>
            <div className="mt-3 rounded-lg bg-white p-3">
              <p className="text-[10px] font-medium uppercase text-slate-400">Vị trí hiện tại</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">
                Gần mức Fibonacci <span className="font-mono text-violet-600">{nearest?.ratio}</span>
              </p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-white p-3">
                <p className="text-[10px] font-medium uppercase text-slate-400">Đến {nearest?.ratio}</p>
                <p className={`mt-1 font-mono text-sm font-bold ${nearestDist >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {nearestDist >= 0 ? '+' : ''}
                  {nearestDist.toFixed(0)} pip
                </p>
              </div>
              <div className="rounded-lg bg-white p-3">
                <p className="text-[10px] font-medium uppercase text-slate-400">Biên độ giá</p>
                <p className="mt-1 font-mono text-sm font-bold text-slate-800">
                  {((fib.high - fib.low) / PIP).toFixed(0)} pip
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Price map */}
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-bold text-slate-900">Fibonacci Price Map</h3>
            <span className="rounded-full bg-violet-50 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-violet-700">
              {fib.type}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-400">Thang giá Fibonacci — từ {fmtPrice(fib.high)} đến {fmtPrice(fib.low)}.</p>
          <div className="mt-4 space-y-1.5">
            {[...fibLevels].sort((a, b) => b.price - a.price).map((l) => {
              const distPips = (l.price - current) / PIP;
              const isNearest = nearest && Math.abs(l.price - nearest.price) < 0.00001;
              return (
                <div
                  key={l.ratio}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isNearest ? 'bg-violet-50 ring-1 ring-violet-200' : 'bg-slate-50'}`}
                >
                  <span className="w-16 shrink-0 font-mono text-xs font-bold text-violet-700">{l.ratio}</span>
                  <span className="shrink-0 font-mono text-sm font-bold tabular-nums text-slate-800">{fmtPrice(l.price)}</span>
                  <span
                    className={`ml-auto shrink-0 font-mono text-[11px] tabular-nums ${
                      distPips > 0 ? 'text-emerald-600' : distPips < 0 ? 'text-rose-600' : 'text-slate-400'
                    }`}
                  >
                    {distPips > 0 ? '+' : ''}
                    {distPips.toFixed(0)} pip
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Use cases */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {FIB_USE_CASES.map((u) => (
          <div key={u.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
              <i className={u.icon} />
            </span>
            <h4 className="mt-3 text-sm font-bold text-slate-800">{u.title}</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{u.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}